import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  List,
  ListItemButton,
  ListItemAvatar,
  Avatar,
  ListItemText,
  Badge,
  Chip,
  IconButton,
  Button,
  Stack,
  Tooltip,
  useTheme,
  CircularProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import RefreshIcon from "@mui/icons-material/Refresh";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import CheckIcon from "@mui/icons-material/Check";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import EventNoteIcon from "@mui/icons-material/EventNote";
import BusinessIcon from "@mui/icons-material/Business";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import { ChatThread } from "../types";
import { format, isToday, isYesterday } from "date-fns";

interface ChatSidebarProps {
  threads: ChatThread[];
  selectedRecipient: string | null;
  onSelectThread: (thread: ChatThread) => void;
  onOpenNewChat: () => void;
  onRefresh: () => void;
  loading: boolean;
  searchQuery: string;
  onSearchChange: (q: string) => void;
  isIdle?: boolean;
}

// Generate consistent beautiful gradient colors for contact avatars
const getAvatarGradient = (str: string) => {
  const gradients = [
    "linear-gradient(135deg, #6366f1 0%, #4338ca 100%)",
    "linear-gradient(135deg, #ec4899 0%, #be185d 100%)",
    "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
    "linear-gradient(135deg, #10b981 0%, #047857 100%)",
    "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
    "linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)",
    "linear-gradient(135deg, #06b6d4 0%, #0e7490 100%)",
    "linear-gradient(135deg, #f97316 0%, #c2410c 100%)",
  ];
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash) % gradients.length;
  return gradients[index];
};

const getInitials = (name: string) => {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
};

const formatThreadTime = (dateStr: string) => {
  try {
    const date = new Date(dateStr);
    if (isToday(date)) {
      return format(date, "HH:mm");
    }
    if (isYesterday(date)) {
      return "Yesterday";
    }
    return format(date, "d MMM");
  } catch {
    return "";
  }
};

export const ChatSidebar: React.FC<ChatSidebarProps> = ({
  threads,
  selectedRecipient,
  onSelectThread,
  onOpenNewChat,
  onRefresh,
  loading,
  searchQuery,
  onSearchChange,
  isIdle = false,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [filterTab, setFilterTab] = useState<"all" | "unread" | "bookings" | "active">("all");

  const filteredThreads = threads.filter((t) => {
    if (filterTab === "unread") return t.unread_count > 0;
    if (filterTab === "bookings") return Boolean(t.booking_id);
    if (filterTab === "active") return t.is_window_open;
    return true;
  });

  const totalUnread = threads.reduce((acc, t) => acc + (t.unread_count || 0), 0);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        backgroundColor: isDark ? "#111827" : "#FFFFFF",
        borderRight: isDark ? "1px solid rgba(255, 255, 255, 0.08)" : "1px solid #E2E8F0",
      }}
    >
      {/* Top Header: Messages Title + Actions */}
      <Box sx={{ p: 2.5, pb: 1.5 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1.2}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                letterSpacing: "-0.5px",
                color: isDark ? "#FFFFFF" : "#0F172A",
                fontSize: "1.45rem",
              }}
            >
              Messages
            </Typography>
            {totalUnread > 0 && (
              <Chip
                label={totalUnread}
                size="small"
                sx={{
                  height: 22,
                  minWidth: 22,
                  backgroundColor: "#25D366",
                  color: "#FFFFFF",
                  fontWeight: 700,
                  fontSize: "0.75rem",
                }}
              />
            )}
            {isIdle && (
              <Tooltip title="Live sync paused due to inactivity. Move mouse or click to resume.">
                <Chip
                  label="Paused (idle)"
                  size="small"
                  onClick={onRefresh}
                  sx={{
                    height: 20,
                    fontSize: "0.68rem",
                    fontWeight: 600,
                    backgroundColor: isDark ? "rgba(245, 158, 11, 0.15)" : "#FEF3C7",
                    color: isDark ? "#FCD34D" : "#B45309",
                    border: "1px solid rgba(245, 158, 11, 0.3)",
                    cursor: "pointer",
                  }}
                />
              </Tooltip>
            )}
          </Stack>

          <Stack direction="row" spacing={0.5}>
            <Tooltip title="New Chat / Add Contact">
              <IconButton
                size="small"
                onClick={onOpenNewChat}
                sx={{
                  color: isDark ? "#94A3B8" : "#64748B",
                  "&:hover": {
                    color: "#724E41",
                    backgroundColor: isDark ? "rgba(255, 255, 255, 0.08)" : "rgba(114, 78, 65, 0.08)",
                  },
                }}
              >
                <PersonAddAlt1Icon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Refresh chats">
              <IconButton
                size="small"
                onClick={onRefresh}
                disabled={loading}
                sx={{
                  color: isDark ? "#94A3B8" : "#64748B",
                  "&:hover": {
                    color: "#724E41",
                    backgroundColor: isDark ? "rgba(255, 255, 255, 0.08)" : "rgba(114, 78, 65, 0.08)",
                  },
                }}
              >
                {loading ? <CircularProgress size={18} color="inherit" /> : <RefreshIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>

        {/* Search Bar */}
        <TextField
          fullWidth
          size="small"
          placeholder="Search name, phone, booking..."
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 19, color: isDark ? "#94A3B8" : "#94A3B8" }} />
              </InputAdornment>
            ),
            endAdornment: searchQuery ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => onSearchChange("")} sx={{ color: isDark ? "#94A3B8" : "#94A3B8" }}>
                  <ClearIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </InputAdornment>
            ) : null,
            sx: {
              borderRadius: "12px",
              backgroundColor: isDark ? "rgba(255, 255, 255, 0.05)" : "#F1F5F9",
              color: isDark ? "#FFFFFF" : "#0F172A",
              "& input": {
                color: isDark ? "#FFFFFF" : "#0F172A",
                fontSize: "0.88rem",
                "&::placeholder": {
                  color: isDark ? "rgba(255, 255, 255, 0.45)" : "#94A3B8",
                  opacity: 1,
                },
              },
              "& fieldset": {
                borderColor: isDark ? "rgba(255, 255, 255, 0.08)" : "#E2E8F0",
              },
              "&:hover fieldset": {
                borderColor: "#724E41",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#724E41",
              },
            },
          }}
        />

        {/* Filter Chips */}
        <Stack direction="row" spacing={1} sx={{ mt: 1.5, overflowX: "auto", pb: 0.5 }}>
          <Chip
            label={`All (${threads.length})`}
            size="small"
            clickable
            onClick={() => setFilterTab("all")}
            sx={{
              fontWeight: 600,
              fontSize: "0.72rem",
              backgroundColor:
                filterTab === "all"
                  ? isDark
                    ? "rgba(114, 78, 65, 0.3)"
                    : "#F7E6D8"
                  : isDark
                  ? "rgba(255, 255, 255, 0.05)"
                  : "#F8FAFC",
              color: filterTab === "all" ? (isDark ? "#E5BFA5" : "#724E41") : (isDark ? "#94A3B8" : "#64748B"),
              border: "1px solid",
              borderColor: filterTab === "all" ? "#724E41" : (isDark ? "rgba(255, 255, 255, 0.08)" : "#E2E8F0"),
            }}
          />
          {totalUnread > 0 && (
            <Chip
              label={`Unread (${totalUnread})`}
              size="small"
              clickable
              onClick={() => setFilterTab("unread")}
              sx={{
                fontWeight: 600,
                fontSize: "0.72rem",
                backgroundColor:
                  filterTab === "unread"
                    ? "rgba(37, 211, 102, 0.15)"
                    : isDark
                    ? "rgba(255, 255, 255, 0.05)"
                    : "#F8FAFC",
                color: filterTab === "unread" ? "#16a34a" : (isDark ? "#94A3B8" : "#64748B"),
                border: "1px solid",
                borderColor: filterTab === "unread" ? "rgba(37, 211, 102, 0.35)" : (isDark ? "rgba(255, 255, 255, 0.08)" : "#E2E8F0"),
              }}
            />
          )}
          <Chip
            label="Active 24h"
            size="small"
            clickable
            onClick={() => setFilterTab("active")}
            sx={{
              fontWeight: 600,
              fontSize: "0.72rem",
              backgroundColor:
                filterTab === "active"
                  ? isDark
                    ? "rgba(114, 78, 65, 0.3)"
                    : "#F7E6D8"
                  : isDark
                  ? "rgba(255, 255, 255, 0.05)"
                  : "#F8FAFC",
              color: filterTab === "active" ? (isDark ? "#E5BFA5" : "#724E41") : (isDark ? "#94A3B8" : "#64748B"),
              border: "1px solid",
              borderColor: filterTab === "active" ? "#724E41" : (isDark ? "rgba(255, 255, 255, 0.08)" : "#E2E8F0"),
            }}
          />
          <Chip
            label="Bookings"
            size="small"
            clickable
            onClick={() => setFilterTab("bookings")}
            sx={{
              fontWeight: 600,
              fontSize: "0.72rem",
              backgroundColor:
                filterTab === "bookings"
                  ? isDark
                    ? "rgba(114, 78, 65, 0.3)"
                    : "#F7E6D8"
                  : isDark
                  ? "rgba(255, 255, 255, 0.05)"
                  : "#F8FAFC",
              color: filterTab === "bookings" ? (isDark ? "#E5BFA5" : "#724E41") : (isDark ? "#94A3B8" : "#64748B"),
              border: "1px solid",
              borderColor: filterTab === "bookings" ? "#724E41" : (isDark ? "rgba(255, 255, 255, 0.08)" : "#E2E8F0"),
            }}
          />
        </Stack>
      </Box>

      {/* Conversations List */}
      <Box sx={{ flexGrow: 1, overflowY: "auto", px: 1.5 }}>
        {filteredThreads.length === 0 ? (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {loading ? "Loading chats..." : "No conversations found."}
            </Typography>
          </Box>
        ) : (
          <List disablePadding>
            {filteredThreads.map((thread) => {
              const isSelected = selectedRecipient === thread.recipient;
              const hasUnread = thread.unread_count > 0;

              return (
                <ListItemButton
                  key={thread.recipient}
                  onClick={() => onSelectThread(thread)}
                  sx={{
                    mb: 0.8,
                    p: 1.4,
                    borderRadius: "14px",
                    backgroundColor: isSelected
                      ? isDark
                        ? "rgba(114, 78, 65, 0.28)"
                        : "rgba(114, 78, 65, 0.08)"
                      : "transparent",
                    boxShadow: isSelected
                      ? "0 2px 8px rgba(114, 78, 65, 0.08)"
                      : "none",
                    border: isSelected
                      ? isDark
                        ? "1px solid rgba(114, 78, 65, 0.4)"
                        : "1px solid rgba(114, 78, 65, 0.2)"
                      : "1px solid transparent",
                    borderLeft: isSelected
                      ? "4px solid #724E41"
                      : "4px solid transparent",
                    transition: "all 0.18s ease-in-out",
                    "&:hover": {
                      backgroundColor: isSelected
                        ? isDark
                          ? "rgba(114, 78, 65, 0.35)"
                          : "rgba(114, 78, 65, 0.12)"
                        : isDark
                        ? "rgba(255, 255, 255, 0.04)"
                        : "#F8FAFC",
                    },
                  }}
                >
                  <ListItemAvatar sx={{ minWidth: 54 }}>
                    <Badge
                      overlap="circular"
                      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                      badgeContent={
                        thread.is_window_open ? (
                          <Tooltip title="24-hour reply window is active">
                            <Box
                              sx={{
                                width: 12,
                                height: 12,
                                borderRadius: "50%",
                                backgroundColor: "#25D366",
                                border: "2px solid #FFFFFF",
                              }}
                            />
                          </Tooltip>
                        ) : null
                      }
                    >
                      <Avatar
                        sx={{
                          width: 44,
                          height: 44,
                          background: getAvatarGradient(thread.contact_name || thread.recipient),
                          fontWeight: 700,
                          fontSize: "0.95rem",
                          color: "#FFFFFF",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                        }}
                      >
                        {getInitials(thread.contact_name || thread.recipient)}
                      </Avatar>
                    </Badge>
                  </ListItemAvatar>

                  <ListItemText
                    disableTypography
                    primary={
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ mb: 0.4 }}
                      >
                        <Typography
                          variant="subtitle2"
                          noWrap
                          sx={{
                            fontWeight: hasUnread ? 700 : 600,
                            color: isDark ? "#FFFFFF" : "#0F172A",
                            fontSize: "0.92rem",
                            maxWidth: "calc(100% - 65px)",
                          }}
                        >
                          {thread.contact_name || thread.formatted_phone}
                        </Typography>

                        <Typography
                          variant="caption"
                          sx={{
                            color: hasUnread
                              ? "#16a34a"
                              : isDark
                              ? "#94A3B8"
                              : "#94A3B8",
                            fontWeight: hasUnread ? 700 : 500,
                            fontSize: "0.72rem",
                          }}
                        >
                          {formatThreadTime(thread.last_message_time)}
                        </Typography>
                      </Stack>
                    }
                    secondary={
                      <Stack spacing={0.4}>
                        {/* Booking tag if linked */}
                        {thread.booking_title && (
                          <Stack direction="row" alignItems="center" spacing={0.5}>
                            <EventNoteIcon sx={{ fontSize: 13, color: isDark ? "#fbbf24" : "#b45309" }} />
                            <Typography
                              variant="caption"
                              noWrap
                              sx={{
                                color: isDark ? "#fbbf24" : "#b45309",
                                fontSize: "0.7rem",
                                fontWeight: 600,
                              }}
                            >
                              {thread.booking_title}
                            </Typography>
                          </Stack>
                        )}

                        {/* Last message preview */}
                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                          spacing={0.5}
                        >
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={0.5}
                            sx={{ minWidth: 0, flexGrow: 1 }}
                          >
                            {thread.last_message_direction === "outbound" && (
                              <Box component="span" sx={{ display: "inline-flex" }}>
                                {thread.last_message_status === "read" ? (
                                  <DoneAllIcon sx={{ fontSize: 14, color: "#53bdeb" }} />
                                ) : thread.last_message_status === "delivered" ? (
                                  <DoneAllIcon sx={{ fontSize: 14, color: isDark ? "#94A3B8" : "#94A3B8" }} />
                                ) : thread.last_message_status === "failed" ? (
                                  <ErrorOutlineIcon sx={{ fontSize: 14, color: "#ef4444" }} />
                                ) : (
                                  <CheckIcon sx={{ fontSize: 14, color: isDark ? "#94A3B8" : "#94A3B8" }} />
                                )}
                              </Box>
                            )}
                            <Typography
                              variant="body2"
                              noWrap
                              sx={{
                                color: hasUnread
                                  ? isDark
                                    ? "#F8FAFC"
                                    : "#0F172A"
                                  : isDark
                                  ? "#94A3B8"
                                  : "#64748B",
                                fontWeight: hasUnread ? 600 : 400,
                                fontSize: "0.82rem",
                              }}
                            >
                              {thread.last_message || "No messages yet"}
                            </Typography>
                          </Stack>

                          {hasUnread && (
                            <Chip
                              label={thread.unread_count}
                              size="small"
                              sx={{
                                height: 18,
                                minWidth: 18,
                                px: 0.3,
                                backgroundColor: "#25D366",
                                color: "#FFFFFF",
                                fontWeight: 700,
                                fontSize: "0.68rem",
                              }}
                            />
                          )}
                        </Stack>
                      </Stack>
                    }
                  />
                </ListItemButton>
              );
            })}
          </List>
        )}
      </Box>
    </Box>
  );
};
